/**
 * Format a US phone number into "+1 (AAA) PPP-NNNN".
 * - Returns the original input if it's not a valid US number.
 * - Accepts inputs with or without country code and with extra formatting chars.
 */
export function formatUSPhoneNumber(input: string): string {
  if (!input) return "";

  const normalized = normalizeUSPhoneNumberToE164(input);
  if (!normalized) {
    return input;
  }

  const national = normalized.slice(2); // drop "+1"

  const areaCode = national.slice(0, 3);
  const prefix = national.slice(3, 6);
  const lineNumber = national.slice(6, 10);

  return `+1 (${areaCode}) ${prefix}-${lineNumber}`;
}

//receives a phone number containing country code with plus sign
export function formatPhoneNumber(input: string | number): string {
    let str = String(input).replace(/[^\d+]/g, "");

    const match = str.match(/^\+?(\d{1})(\d{3})(\d+)?$/);
    if (!match) return str; // fallback: return original

    const [, country, area, rest] = match;
    return `+${country} (${area}) ${rest}`;
}

// receives an assumed US number that may or may not contain US country code, checks for validity as a US number
export function isValidUSPhoneNumber(input: string): boolean {
    if (!input) return false;

    const mainPart = input.split(/ext\.?|x/i)[0];

    const digits = mainPart.replace(/\D/g, "");

    if (digits.length === 11 && digits.startsWith("1")) {
        return isValidNanpTenDigit(digits.slice(1));
    }

    if (digits.length === 10) {
        return isValidNanpTenDigit(digits);
    }

    return false;
}

function isValidNanpTenDigit(num: string): boolean {
    if (!/^\d{10}$/.test(num)) return false;

    const area = num.slice(0, 3);
    const exchange = num.slice(3, 6);
    const line = num.slice(6);

    // Area code: NXX where N = 2–9, X = 0–9, middle digit 0–8
    if (!/^[2-9][0-8][0-9]$/.test(area)) return false;

    // Exchange code: NXX where N = 2–9
    if (!/^[2-9][0-9]{2}$/.test(exchange)) return false;

    // Line number: any 4 digits
    if (!/^[0-9]{4}$/.test(line)) return false;

    return true;
}

/**
 * Normalize a US phone number to E.164 (+1XXXXXXXXXX).
 * Returns null if the input is not a valid US number.
 */
export function normalizeUSPhoneNumberToE164(input: string): string | null {
  if (!input) return null;

  const mainPart = input.split(/ext\.?|x/i)[0];
  const digits = mainPart.replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) {
    const national = digits.slice(1);
    if (!isValidNanpTenDigit(national)) return null;
    return `+${digits}`; // +1XXXXXXXXXX
  }

  if (digits.length === 10) {
    if (!isValidNanpTenDigit(digits)) return null;
    return `+1${digits}`;
  }

  return null;
}